import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useAppSelector } from 'hooks/reduxHooks'
import LoyaltyTierBadge from './LoyaltyTierBadge'
import useLoyaltyConfig from 'loyalty/hooks/useLoyaltyConfig'
import getIsLoyaltyEsimEnabled from 'loyalty/selectors/getIsLoyaltyEsimEnabled'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'

function LoyaltyLandingPageTierInformation() {
  const intl = useIntl()
  const [config, configFetching, configError] = useLoyaltyConfig()

  const bronzeTitle = useMemo(
    () =>
      intl.formatMessage({
        id: 'QiLgovgWAHZsy1Lb6Z0U',
        defaultMessage: 'Your journey starts here',
        description: 'Title for Bronze tier loyalty information in the loyalty section',
      }),
    [intl],
  )

  const isEsimEnabled = useAppSelector(getIsLoyaltyEsimEnabled)

  const bronzeDescription = useMemo(
    () =>
      isEsimEnabled
        ? intl.formatMessage({
            id: 'dqF1pCG9Tzg1fk4rNbvN',
            defaultMessage: 'Start earning points and status credits on bookings, and receive a free travel eSIM on international trips.',
            description: 'Description summarizing the Bronze tier loyalty benefits for new members',
          })
        : intl.formatMessage({
            id: 'mSiFyyddq0iSuwINoBQr',
            defaultMessage: 'Start earning points and status credits on bookings.',
            description: 'Description summarizing the Bronze tier loyalty benefits without eSIM',
          }),
    [intl, isEsimEnabled],
  )

  const silverTitle = useMemo(
    () =>
      intl.formatMessage({
        id: 'ShbqP46Ed9gEp8Pg3V3d',
        defaultMessage: 'Step up the benefits',
        description: 'Title for Silver tier loyalty information in the loyalty section',
      }),
    [intl],
  )

  const silverDescriptionWithLounge = useMemo(
    () =>
      intl.formatMessage({
        id: 'JCfd8L_QHDxK5NBa7uNw',
        defaultMessage: 'Access added perks like lounge passes, tour credits, and discounted photoshoots.',
        description: 'Description for Silver tier loyalty benefits when lounge passes are enabled',
      }),
    [intl],
  )

  const silverDescriptionWithoutLounge = useMemo(
    () =>
      intl.formatMessage({
        id: 'XgMKh0hU5ip6U5ZVmpBS',
        defaultMessage: 'Access added perks like tour credits, and discounted photoshoots.',
        description: 'Description for Silver tier loyalty benefits when lounge passes are not enabled',
      }),
    [intl],
  )

  const goldTitle = useMemo(
    () =>
      intl.formatMessage({
        id: 'y3eSMujU8TOe-acc3eqe',
        defaultMessage: 'A new Gold standard',
        description: 'Title for Gold tier loyalty information in the loyalty program section',
      }),
    [intl],
  )

  const goldDescription = useMemo(
    () =>
      intl.formatMessage({
        id: 'kpuisqYy3iZFxu0eunoI',
        defaultMessage: 'Enjoy elevated benefits including free room upgrades and chauffeur airport transfers.',
        description: 'Description for Gold tier loyalty benefits in the loyalty section',
      }),
    [intl],
  )

  const platinumTitle = useMemo(
    () =>
      intl.formatMessage({
        id: 'WHwJVE96IqogmfWk2__h',
        defaultMessage: 'The complete VIP experience',
        description: 'Title for Platinum tier loyalty information in the loyalty section',
      }),
    [intl],
  )

  const platinumDescription = useMemo(
    () =>
      intl.formatMessage({
        id: '_yCmEHPDz5MQh2eJ3SK7',
        defaultMessage: 'Indulge in unlimited chauffeur transfers and additional room upgrades for the ultimate escape.',
        description: 'Description for Platinum tier loyalty benefits on the promotional landing page',
      }),
    [intl],
  )

  if (configFetching) return <LoadingIndicator />
  if (!config || configError) return null

  return (
    <Group direction="vertical" tabletDirection="horizontal" gap={48} tabletGap={36}>
      <VerticalSpacer gap={16}>
        <LoyaltyTierBadge size="large" tier="bronze" />
        <VerticalSpacer gap={4}>
          <Heading variant="heading5">
            {bronzeTitle}
          </Heading>
          <BodyText variant="medium">
            {bronzeDescription}
          </BodyText>
        </VerticalSpacer>
      </VerticalSpacer>
      <VerticalSpacer gap={16}>
        <LoyaltyTierBadge size="large" tier="silver" />
        <VerticalSpacer gap={4}>
          <Heading variant="heading5">
            {silverTitle}
          </Heading>
          {config.regionEnabledBenefits.lounge_pass && (
            <BodyText variant="medium">
              {silverDescriptionWithLounge}
            </BodyText>
          )}
          {!config.regionEnabledBenefits.lounge_pass && (
            <BodyText variant="medium">
              {silverDescriptionWithoutLounge}
            </BodyText>
          )}
        </VerticalSpacer>
      </VerticalSpacer>
      <VerticalSpacer gap={16}>
        <LoyaltyTierBadge size="large" tier="gold" />
        <VerticalSpacer gap={4}>
          <Heading variant="heading5">
            {goldTitle}
          </Heading>
          <BodyText variant="medium">
            {goldDescription}
          </BodyText>
        </VerticalSpacer>
      </VerticalSpacer>
      <VerticalSpacer gap={16}>
        <LoyaltyTierBadge size="large" tier="platinum" />
        <VerticalSpacer gap={4}>
          <Heading variant="heading5">
            {platinumTitle}
          </Heading>
          <BodyText variant="medium">
            {platinumDescription}
          </BodyText>
        </VerticalSpacer>
      </VerticalSpacer>
    </Group>
  )
}

export default LoyaltyLandingPageTierInformation
